import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Download,
  Clock,
  Cpu,
  FileText,
  ChevronDown,
  Search,
  Check,
  ScrollText,
  X,
  Sparkles,
  Loader2,
  Filter,
} from 'lucide-react'
import {
  extractionApi,
  type ExtractionJob,
  type JobItem,
  type DocumentSummary,
} from '@/api/extraction'
import { documentsApi } from '@/api/documents'
import { fieldsApi } from '@/api/fields'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ModelBadge } from '@/components/ui/model-badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatDateTime } from '@/lib/utils'

interface Props {
  projectId: string
  filterDocumentId?: string | null
  onClearFilter?: () => void
}

function confidenceColor(c: number | null): string {
  if (c === null) return 'text-gray-500 bg-gray-50'
  if (c >= 0.8) return 'text-green-600 bg-green-50'
  if (c >= 0.5) return 'text-yellow-600 bg-yellow-50'
  return 'text-red-600 bg-red-50'
}

function statusColor(s: string): string {
  if (s === 'completed') return 'text-green-600 bg-green-50'
  if (s === 'failed') return 'text-red-600 bg-red-50'
  if (s === 'running') return 'text-blue-600 bg-blue-50'
  return 'text-gray-600 bg-gray-50'
}

function formatDuration(s: string | null, e: string | null): string {
  if (!s || !e) return '--'
  const sec = Math.round((new Date(e).getTime() - new Date(s).getTime()) / 1000)
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`
}

function formatRunLabel(job: ExtractionJob, idx: number, total: number): string {
  const date = formatDateTime(job.created_at)
  const mode = job.extraction_mode === 'batch' ? 'batch' : 'per-field'
  return `Run #${total - idx} - ${mode} - ${job.selected_model || '?'} - ${date}`
}

export default function ExtractionResults({ projectId, filterDocumentId, onClearFilter }: Props) {
  const queryClient = useQueryClient()
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [runDropdownOpen, setRunDropdownOpen] = useState(false)
  const [runSearch, setRunSearch] = useState('')
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const [showLogsDialog, setShowLogsDialog] = useState(false)
  const [resultsPageSize, setResultsPageSize] = useState(10)
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1)
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())
  const [selectAllDocs, setSelectAllDocs] = useState(false)  // true = all docs in project selected (beyond current page)

  // Document filter state
  const [docFilterOpen, setDocFilterOpen] = useState(false)
  const [docFilterSearch, setDocFilterSearch] = useState('')
  const [localFilterDocId, setLocalFilterDocId] = useState<string | null>(null)

  const runRef = useRef<HTMLDivElement>(null)
  const exportRef = useRef<HTMLDivElement>(null)
  const docFilterRef = useRef<HTMLDivElement>(null)

  // The active document filter: prop takes priority (navigation from Documents tab), then local filter
  const activeDocumentFilter = filterDocumentId || localFilterDocId

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (runRef.current && !runRef.current.contains(e.target as Node)) setRunDropdownOpen(false)
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) setExportDropdownOpen(false)
      if (docFilterRef.current && !docFilterRef.current.contains(e.target as Node)) setDocFilterOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const { data: jobs = [] } = useQuery({
    queryKey: ['extraction-jobs', projectId],
    queryFn: () => extractionApi.listJobs(projectId),
  })

  const { data: documents = [] } = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => documentsApi.list(projectId),
  })

  const { data: fields = [] } = useQuery({
    queryKey: ['fields', projectId],
    queryFn: () => fieldsApi.list(projectId),
  })

  const hasRunningJob = jobs.some((j) => j.status === 'running' || j.status === 'queued')

  const summaryMutation = useMutation({
    mutationFn: (docIds: string[]) => extractionApi.generateSummaries(projectId, docIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-summaries', projectId] })
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      setSelectedDocIds(new Set())
    },
  })

  const handleExport = async (
    format: 'csv' | 'excel',
    options?: { include_summary?: boolean; summary_only?: boolean }
  ) => {
    const token = localStorage.getItem('access_token')
    let url = `/api/exports/${format}?project_id=${projectId}`
    if (selectedJobId) url += `&job_id=${selectedJobId}`
    if (options?.include_summary) url += '&include_summary=true'
    if (options?.summary_only) url += '&summary_only=true'
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = `export.${format === 'csv' ? 'csv' : 'xlsx'}`
    a.click()
    URL.revokeObjectURL(blobUrl)
  }

  // Server-side paginated results (compact format)
  const { data: paginatedResults, isLoading } = useQuery({
    queryKey: ['results', projectId, selectedJobId, activeDocumentFilter, resultsCurrentPage, resultsPageSize],
    queryFn: () =>
      extractionApi.getResults(
        projectId,
        activeDocumentFilter || undefined,
        selectedJobId || undefined,
        resultsCurrentPage,
        resultsPageSize
      ),
    refetchInterval: hasRunningJob ? 5000 : false,
  })

  const compactDocuments = Array.isArray(paginatedResults?.documents) ? paginatedResults.documents : []
  const totalPages = paginatedResults?.total_pages ?? 1

  // Fetch document summaries lazily (only after results load)
  const [summariesLoaded, setSummariesLoaded] = useState(false)
  const { data: documentSummaries = [] } = useQuery({
    queryKey: ['document-summaries', projectId, selectedJobId],
    queryFn: () => extractionApi.getDocumentSummaries(projectId, selectedJobId || undefined),
    enabled: summariesLoaded && jobs.length > 0,
  })
  // Trigger summaries load after results arrive
  useEffect(() => {
    if (compactDocuments.length > 0) setSummariesLoaded(true)
  }, [compactDocuments.length])

  const selectedJob = selectedJobId ? jobs.find((j) => j.id === selectedJobId) : null

  // Job items for logs dialog — only fetch when dialog is open
  const logsJobId = selectedJobId || (jobs.length > 0 ? jobs[0].id : null)
  const { data: jobItems = [] } = useQuery({
    queryKey: ['job-items', logsJobId],
    queryFn: () => extractionApi.getJobItems(logsJobId!),
    enabled: showLogsDialog && !!logsJobId,
  })

  // Build pivoted data from compact format
  const docMap = Object.fromEntries(documents.map((d) => [d.id, d]))
  const docFieldNames = fields.filter((f) => !f.is_line_item).map((f) => f.display_name || f.name)
  const lineItemFieldNames = fields.filter((f) => f.is_line_item).map((f) => f.display_name || f.name)
  const allFieldNames = [...docFieldNames, ...lineItemFieldNames]
  const hasLineItems = lineItemFieldNames.length > 0

  interface PivotedRow {
    docId: string
    docName: string
    rowIndex: number | null
    isFirstRowForDoc: boolean
    docRowSpan: number
    fieldValues: Record<string, { value: string | null; confidence: number | null }>
    avgConfidence: number | null
  }

  const pivotedRows = useMemo(() => {
    const tableRows: PivotedRow[] = []

    for (const doc of compactDocuments) {
      if (!doc || !doc.rows || !Array.isArray(doc.rows) || doc.rows.length === 0) continue
      const docRowSpan = doc.rows.length

      for (let i = 0; i < doc.rows.length; i++) {
        const compactRow = doc.rows[i]
        if (!compactRow) continue

        const fieldValues: Record<string, { value: string | null; confidence: number | null }> = {}
        let totalConf = 0
        let confCount = 0

        const fields = compactRow.fields || {}
        for (const [fieldName, fv] of Object.entries(fields)) {
          if (!fv) continue
          fieldValues[fieldName] = { value: fv.value ?? null, confidence: fv.confidence ?? null }
          if (fv.confidence !== null && fv.confidence !== undefined) {
            totalConf += fv.confidence
            confCount++
          }
        }

        tableRows.push({
          docId: doc.document_id,
          docName: doc.document_name,
          rowIndex: compactRow.row_index ?? null,
          isFirstRowForDoc: i === 0,
          docRowSpan,
          fieldValues,
          avgConfidence: confCount > 0 ? totalConf / confCount : null,
        })
      }
    }

    return tableRows
  }, [compactDocuments])

  // Reset to page 1 when run or filter changes
  useEffect(() => {
    setResultsCurrentPage(1)
  }, [selectedJobId, activeDocumentFilter, resultsPageSize])

  const filteredJobs = jobs.filter((job, idx) => {
    if (!runSearch) return true
    return formatRunLabel(job, idx, jobs.length).toLowerCase().includes(runSearch.toLowerCase())
  })

  const currentRunLabel = selectedJobId
    ? (() => {
        const idx = jobs.findIndex((j) => j.id === selectedJobId)
        return idx >= 0 ? formatRunLabel(jobs[idx], idx, jobs.length) : 'Selected run'
      })()
    : 'Latest'

  // Documents available for the filter dropdown
  const filterDocuments = useMemo(() => {
    const docs = documents.filter((d) => {
      if (!docFilterSearch) return true
      return d.original_filename.toLowerCase().includes(docFilterSearch.toLowerCase())
    })
    return docs.sort((a, b) => a.original_filename.localeCompare(b.original_filename))
  }, [documents, docFilterSearch])

  const activeFilterDocName = activeDocumentFilter
    ? docMap[activeDocumentFilter]?.original_filename || activeDocumentFilter
    : null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Extraction Results</h2>
        <div className="flex items-center gap-2">
          {/* Run selector */}
          {jobs.length > 0 && (
            <div className="relative" ref={runRef}>
              <Button variant="outline" size="sm" onClick={() => { setRunDropdownOpen(!runDropdownOpen); setRunSearch('') }} className="max-w-xs">
                <Clock className="h-3.5 w-3.5 mr-1 shrink-0" />
                <span className="truncate max-w-[180px]">{currentRunLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 ml-1 shrink-0" />
              </Button>
              {runDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-96 bg-background border rounded-lg shadow-lg z-50">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input placeholder="Search runs..." value={runSearch} onChange={(e) => setRunSearch(e.target.value)} className="pl-7 h-8 text-sm" autoFocus />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    <button onClick={() => { setSelectedJobId(null); setRunDropdownOpen(false) }}
                      className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 ${!selectedJobId ? 'bg-muted font-medium' : ''}`}>
                      {!selectedJobId && <Check className="h-3.5 w-3.5 shrink-0" />}
                      <span className={!selectedJobId ? '' : 'ml-5'}>Latest (current results)</span>
                    </button>
                    {filteredJobs.map((job) => {
                      const oi = jobs.indexOf(job)
                      return (
                        <button key={job.id} onClick={() => { setSelectedJobId(job.id); setRunDropdownOpen(false) }}
                          className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 ${selectedJobId === job.id ? 'bg-muted font-medium' : ''}`}>
                          {selectedJobId === job.id && <Check className="h-3.5 w-3.5 shrink-0" />}
                          <span className={selectedJobId === job.id ? '' : 'ml-5'}>{formatRunLabel(job, oi, jobs.length)}</span>
                          <Badge variant="outline" className={`text-xs ml-auto shrink-0 ${statusColor(job.status)}`}>{job.status}</Badge>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Logs button */}
          {jobs.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowLogsDialog(true)} title="View extraction logs">
              <ScrollText className="h-3.5 w-3.5 mr-1" /> Logs
            </Button>
          )}
          {/* Model badge + duration */}
          {(() => {
            const job = selectedJob || (jobs.length > 0 ? jobs[0] : null)
            if (!job) return null
            const dur = formatDuration(job.started_at, job.completed_at)
            return (
              <div className="flex items-center gap-2">
                {job.selected_model && <ModelBadge modelName={job.selected_model} />}
                {dur !== '--' && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />{dur}
                  </span>
                )}
              </div>
            )
          })()}
          {/* Export */}
          <div className="relative" ref={exportRef}>
            <Button variant="outline" size="sm" onClick={() => setExportDropdownOpen(!exportDropdownOpen)}>
              <Download className="h-4 w-4 mr-1" /> Export <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
            {exportDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-background border rounded-lg shadow-lg z-50 p-1">
                <button onClick={() => { handleExport('csv'); setExportDropdownOpen(false) }}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted w-full">
                  <Download className="h-3.5 w-3.5" /> Export as CSV
                </button>
                <button onClick={() => { handleExport('excel'); setExportDropdownOpen(false) }}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted w-full">
                  <Download className="h-3.5 w-3.5" /> Export as Excel
                </button>
                <div className="border-t my-1" />
                <button onClick={() => { handleExport('csv', { include_summary: true }); setExportDropdownOpen(false) }}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted w-full">
                  <Sparkles className="h-3.5 w-3.5" /> With Summaries (CSV)
                </button>
                <button onClick={() => { handleExport('excel', { include_summary: true }); setExportDropdownOpen(false) }}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted w-full">
                  <Sparkles className="h-3.5 w-3.5" /> With Summaries (Excel)
                </button>
                <button onClick={() => { handleExport('csv', { summary_only: true }); setExportDropdownOpen(false) }}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-muted w-full">
                  <ScrollText className="h-3.5 w-3.5" /> Summaries Only (CSV)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Run metadata */}
      {selectedJob && (
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-1.5">
                <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                {selectedJob.selected_model ? (
                  <ModelBadge modelName={selectedJob.selected_model} />
                ) : (
                  <span className="font-medium text-foreground">Unknown</span>
                )}
              </div>
              <Badge variant="outline" className="text-xs">{selectedJob.extraction_mode === 'batch' ? 'Batch' : 'Per-field'}</Badge>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
                <span>{selectedJob.processed_documents}/{selectedJob.total_documents} docs</span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>{formatDuration(selectedJob.started_at, selectedJob.completed_at)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Searchable document filter bar */}
      <div className="flex items-center gap-2">
        <div className="relative" ref={docFilterRef}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setDocFilterOpen(!docFilterOpen); setDocFilterSearch('') }}
            className={activeFilterDocName ? 'border-primary/50' : ''}
          >
            <Filter className="h-3.5 w-3.5 mr-1 shrink-0" />
            <span className="truncate max-w-[200px]">
              {activeFilterDocName || 'Filter by document'}
            </span>
            <ChevronDown className="h-3.5 w-3.5 ml-1 shrink-0" />
          </Button>
          {docFilterOpen && (
            <div className="absolute left-0 top-full mt-1 w-80 bg-background border rounded-lg shadow-lg z-50">
              <div className="p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={docFilterSearch}
                    onChange={(e) => setDocFilterSearch(e.target.value)}
                    className="pl-7 h-8 text-sm"
                    autoFocus
                  />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-1">
                <button
                  onClick={() => {
                    setLocalFilterDocId(null)
                    if (filterDocumentId && onClearFilter) onClearFilter()
                    setDocFilterOpen(false)
                  }}
                  className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 ${!activeDocumentFilter ? 'bg-muted font-medium' : ''}`}
                >
                  {!activeDocumentFilter && <Check className="h-3.5 w-3.5 shrink-0" />}
                  <span className={!activeDocumentFilter ? '' : 'ml-5'}>All documents</span>
                </button>
                {filterDocuments.map((doc) => {
                  const isActive = activeDocumentFilter === doc.id
                  return (
                    <button
                      key={doc.id}
                      onClick={() => {
                        if (filterDocumentId && onClearFilter) onClearFilter()
                        setLocalFilterDocId(doc.id)
                        setDocFilterOpen(false)
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 ${isActive ? 'bg-muted font-medium' : ''}`}
                    >
                      {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
                      <span className={isActive ? '' : 'ml-5'}>
                        <FileText className="h-3 w-3 inline mr-1.5 text-muted-foreground" />
                        {doc.original_filename}
                      </span>
                    </button>
                  )
                })}
                {filterDocuments.length === 0 && (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No documents match</p>
                )}
              </div>
            </div>
          )}
        </div>
        {activeDocumentFilter && (
          <button
            onClick={() => {
              setLocalFilterDocId(null)
              if (filterDocumentId && onClearFilter) onClearFilter()
            }}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title="Clear document filter"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        {/* Document filter badge from prop (navigation from Documents tab) */}
        {filterDocumentId && (
          <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-3 py-1.5">
            <span className="text-muted-foreground text-xs">Navigated from:</span>
            <Badge variant="outline" className="text-xs">
              {docMap[filterDocumentId]?.original_filename || filterDocumentId}
            </Badge>
          </div>
        )}
      </div>

      {hasRunningJob && (
        <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
          <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
          Extraction in progress — results updating automatically
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading results...</div>
      ) : pivotedRows.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground">No extraction results yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Upload documents and process them to see results here.</p>
        </div>
      ) : (
        (() => {
          const uniqueDocIds = [...new Set(pivotedRows.map(r => r.docId))]
          const allSelected = uniqueDocIds.length > 0 && uniqueDocIds.every(id => selectedDocIds.has(id))
          const summaryMap = Object.fromEntries(documentSummaries.map(s => [s.document_id, s.summary]))

          return (<>
        {/* Summary action bar */}
        {(selectedDocIds.size > 0 || selectAllDocs) && (
          <div className="flex flex-col gap-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2 text-sm">
            <div className="flex items-center gap-3">
              <span className="font-medium">
                {selectAllDocs ? 'All documents selected' : `${selectedDocIds.size} document${selectedDocIds.size > 1 ? 's' : ''} selected`}
              </span>
              <Button
                size="sm"
                onClick={() => {
                  const count = selectAllDocs ? 'all' : selectedDocIds.size
                  if (confirm(`Generate AI summary for ${count} document(s)? This charges 1 chat credit per document.`)) {
                    if (selectAllDocs) {
                      // Pass special flag — backend handles "all"
                      extractionApi.generateSummaries(projectId, ['__all__']).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['document-summaries', projectId] })
                        setSelectAllDocs(false)
                        setSelectedDocIds(new Set())
                      })
                    } else {
                      summaryMutation.mutate([...selectedDocIds])
                    }
                  }
                }}
                disabled={summaryMutation.isPending}
              >
                {summaryMutation.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Generating...</>
                ) : (
                  <><Sparkles className="h-3.5 w-3.5 mr-1" /> Generate Summary</>
                )}
              </Button>
              <button onClick={() => { setSelectedDocIds(new Set()); setSelectAllDocs(false) }} className="text-xs text-muted-foreground hover:text-foreground ml-auto">Clear selection</button>
            </div>
            {/* Select all beyond current page */}
            {!selectAllDocs && allSelected && uniqueDocIds.length === Number(resultsPageSize) && (
              <button
                onClick={() => setSelectAllDocs(true)}
                className="text-xs text-primary hover:underline text-left"
              >
                Select all documents across all pages
              </button>
            )}
          </div>
        )}

        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-3 w-8">
                  <input
                    type="checkbox"
                    checked={selectAllDocs || allSelected}
                    onChange={() => {
                      if (selectAllDocs || allSelected) {
                        setSelectedDocIds(new Set())
                        setSelectAllDocs(false)
                      } else {
                        setSelectedDocIds(new Set(uniqueDocIds))
                      }
                    }}
                  />
                </th>
                <th className="text-left p-3 font-medium whitespace-nowrap sticky left-0 bg-muted/50">Document</th>
                {hasLineItems && (
                  <th className="text-center p-3 font-medium whitespace-nowrap">#</th>
                )}
                {allFieldNames.map((name) => (
                  <th key={name} className="text-left p-3 font-medium whitespace-nowrap">{name}</th>
                ))}
                <th className="text-center p-3 font-medium whitespace-nowrap">Avg Confidence</th>
              </tr>
            </thead>
            <tbody>
              {pivotedRows.map((row, rowIdx) => (
                <tr key={`${row.docId}-${row.rowIndex ?? 'doc'}`} className="border-t hover:bg-muted/20">
                  {row.isFirstRowForDoc && (
                    <td className="p-3 align-top" rowSpan={row.docRowSpan}>
                      <input
                        type="checkbox"
                        checked={selectAllDocs || selectedDocIds.has(row.docId)}
                        onChange={() => {
                          if (selectAllDocs) {
                            setSelectAllDocs(false)
                            // Deselect this one, keep the rest on this page
                            const without = new Set(uniqueDocIds)
                            without.delete(row.docId)
                            setSelectedDocIds(without)
                            return
                          }
                          setSelectedDocIds(prev => {
                            const next = new Set(prev)
                            if (next.has(row.docId)) next.delete(row.docId)
                            else next.add(row.docId)
                            return next
                          })
                        }}
                      />
                    </td>
                  )}
                  {row.isFirstRowForDoc && (
                    <td
                      className="p-3 font-medium text-xs sticky left-0 bg-background max-w-[250px] align-top"
                      title={row.docName}
                      rowSpan={row.docRowSpan}
                    >
                      <div className="truncate">{row.docName}</div>
                      {summaryMap[row.docId] && (
                        <details className="mt-1">
                          <summary className="text-[10px] text-primary cursor-pointer hover:underline flex items-center gap-1">
                            <Sparkles className="h-2.5 w-2.5" /> Summary
                          </summary>
                          <p className="text-[11px] text-muted-foreground mt-1 whitespace-normal leading-relaxed max-h-24 overflow-y-auto">
                            {summaryMap[row.docId]}
                          </p>
                        </details>
                      )}
                    </td>
                  )}
                  {hasLineItems && (
                    <td className="p-3 text-center text-xs text-muted-foreground">
                      {row.rowIndex !== null ? row.rowIndex + 1 : ''}
                    </td>
                  )}
                  {allFieldNames.map((fname) => {
                    const cell = row.fieldValues[fname]
                    return (
                      <td key={fname} className="p-3 max-w-[200px]">
                        {cell ? (
                          <div className="flex items-center gap-1.5">
                            <span className="truncate block" title={cell.value || ''}>
                              {cell.value || <em className="text-muted-foreground">null</em>}
                            </span>
                            {cell.confidence !== null && (
                              <Badge variant="outline" className={`text-[10px] px-1 py-0 shrink-0 ${confidenceColor(cell.confidence)}`}>
                                {(cell.confidence * 100).toFixed(0)}%
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                    )
                  })}
                  <td className="p-3 text-center">
                    {row.avgConfidence !== null ? (
                      <Badge variant="outline" className={`text-xs ${confidenceColor(row.avgConfidence)}`}>
                        {(row.avgConfidence * 100).toFixed(0)}%
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">--</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination controls -- always show when there are results */}
        <div className="flex items-center justify-between px-3 py-2 border-t text-sm">
          <span className="text-muted-foreground">
            Page {resultsCurrentPage} · {pivotedRows.length > 0 ? `${new Set(pivotedRows.map(r => r.docId)).size} documents` : 'No results'}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Docs/page:</span>
            <select
              value={resultsPageSize}
              onChange={(e) => { setResultsPageSize(Number(e.target.value)); setResultsCurrentPage(1) }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <Button variant="outline" size="sm" disabled={resultsCurrentPage <= 1} onClick={() => setResultsCurrentPage((p) => p - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={resultsCurrentPage >= totalPages} onClick={() => setResultsCurrentPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
          </>)
        })()
      )}

      {/* Extraction Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Extraction Logs</DialogTitle>
          </DialogHeader>
          {jobItems.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No log entries found for this run.</p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 font-medium">Document</th>
                    <th className="text-left p-2 font-medium">Status</th>
                    <th className="text-left p-2 font-medium">Model</th>
                    <th className="text-center p-2 font-medium">File Sent</th>
                    <th className="text-right p-2 font-medium">Tokens</th>
                    <th className="text-right p-2 font-medium">Duration</th>
                    <th className="text-center p-2 font-medium">Confidence</th>
                    <th className="text-left p-2 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {jobItems.map((item) => {
                    const log = item.extraction_log
                    return (
                      <tr key={item.id} className="border-t hover:bg-muted/20">
                        <td className="p-2 max-w-[180px] truncate" title={item.document_name || ''}>
                          {item.document_name || item.document_id}
                        </td>
                        <td className="p-2">
                          <Badge variant="outline" className={`text-xs ${statusColor(item.status)}`}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className="p-2 text-xs">
                          {log?.model ? <ModelBadge modelName={log.model} /> : '--'}
                        </td>
                        <td className="p-2 text-center text-xs">
                          {log ? (
                            log.file_sent ? (
                              <span className="text-green-600">Yes</span>
                            ) : (
                              <span className="text-yellow-600">Text</span>
                            )
                          ) : '--'}
                        </td>
                        <td className="p-2 text-right text-xs text-muted-foreground">
                          {log?.tokens_used ?? item.tokens_used ?? '--'}
                        </td>
                        <td className="p-2 text-right text-xs text-muted-foreground">
                          {log?.duration_seconds != null ? `${log.duration_seconds}s` : '--'}
                        </td>
                        <td className="p-2 text-center">
                          {item.avg_confidence != null ? (
                            <Badge variant="outline" className={`text-xs ${confidenceColor(item.avg_confidence)}`}>
                              {(item.avg_confidence * 100).toFixed(0)}%
                            </Badge>
                          ) : '--'}
                        </td>
                        <td className="p-2 text-xs text-destructive max-w-[200px] truncate" title={item.error_message || log?.error || ''}>
                          {item.error_message || log?.error || ''}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* LLM Call Details (superadmin only -- shown when llm_calls data is present) */}
          {jobItems.some((item) => item.extraction_log?.llm_calls?.length) && (
            <div className="mt-4 space-y-3">
              <h3 className="text-sm font-semibold">LLM Request / Response Details</h3>
              {jobItems.filter((item) => item.extraction_log?.llm_calls?.length).map((item) => (
                <div key={item.id} className="border rounded-lg">
                  <div className="bg-muted/50 px-3 py-2 text-xs font-medium border-b">
                    {item.document_name || item.document_id}
                  </div>
                  {item.extraction_log!.llm_calls!.map((call, idx) => (
                    <div key={idx} className="border-b last:border-0 p-3 space-y-2">
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="outline" className="text-[10px]">{call.call_type}</Badge>
                        <span className="text-muted-foreground">{call.model}</span>
                      </div>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Request Prompt</summary>
                        <pre className="mt-1 p-2 bg-muted rounded text-[11px] whitespace-pre-wrap max-h-48 overflow-y-auto">{call.request_prompt}</pre>
                      </details>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Raw Response</summary>
                        <pre className="mt-1 p-2 bg-muted rounded text-[11px] whitespace-pre-wrap max-h-48 overflow-y-auto">{call.raw_response}</pre>
                      </details>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
